import React, { useState, useEffect } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { useAuth } from '../../Providers/AuthProvider';
import { Confirm } from './../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';

export default function ZoneListsManager({ getOutOfTheDynamic }) {
    const { authenticatedFetch } = useAuth();
    const { showMessage } = useFloatMessage();

    const [zoneLists, setZoneLists] = useState([]);
    const [selectedList, setSelectedList] = useState(null);

    // Form states
    const [listName, setListName] = useState('');
    const [zones, setZones] = useState([]); // [{ id: number, displayName: string, internalName: string, color: string }]

    // UI States
    const [loading, setLoading] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);

    // Confirm Modal states
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

    const fetchZoneLists = async () => {
        setLoading(true);
        try {
            const res = await authenticatedFetch(`${API_URL}v2/zonelists`);
            if (res.ok) {
                const data = await res.json();
                setZoneLists(data);
            } else {
                console.error("Failed to fetch zone lists", res.statusText);
            }
        } catch (error) {
            console.error("Error fetching zone lists:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchZoneLists();
    }, []);

    const handleSelectField = (list) => {
        setSelectedList(list);
        setListName(list.name);
        try {
            const parsedZones = JSON.parse(list.zonesJson);
            // Asegurar que cada zona tenga un ID o clave temporal
            setZones(parsedZones.map((z, idx) => ({
                tempId: z.tempId || Math.random().toString(),
                id: z.id || idx + 1,
                displayName: z.displayName || '',
                internalName: z.internalName || '',
                color: z.color || '#4CC9F0'
            })));
        } catch (e) {
            console.error("Error parsing zonesJson", e);
            setZones([]);
        }
    };

    const handleCancelEdit = () => {
        setSelectedList(null);
        setListName('');
        setZones([]);
    };

    const handleAddZone = () => {
        const nextId = zones.length > 0 ? Math.max(...zones.map(z => z.id)) + 1 : 1;
        setZones([
            ...zones,
            {
                tempId: Math.random().toString(),
                id: nextId,
                displayName: '',
                internalName: '',
                color: '#4CC9F0'
            }
        ]);
    };

    const handleRemoveZone = (tempId) => {
        setZones(zones.filter(z => z.tempId !== tempId));
    };

    const handleZoneChange = (tempId, field, value) => {
        setZones(zones.map(z => {
            if (z.tempId === tempId) {
                const updatedZone = { ...z, [field]: value };
                // Si el usuario escribe displayName y el internalName está vacío o no modificado manualmente, autogenerar
                if (field === 'displayName') {
                    const slug = value.trim()
                        .toLowerCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                        .replace(/[^a-z0-9_ ]/g, '')
                        .replace(/\s+/g, '_');
                    updatedZone.internalName = slug;
                }
                return updatedZone;
            }
            return z;
        }));
    };

    const validateForm = () => {
        if (!listName.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Ingresa el nombre del listado de zonas." });
            return false;
        }

        if (zones.length === 0) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Debes añadir al menos una zona." });
            return false;
        }

        const internalNames = new Set();
        for (let i = 0; i < zones.length; i++) {
            const z = zones[i];
            if (!z.displayName.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La zona #${i + 1} no tiene etiqueta (displayName).` });
                return false;
            }
            if (!z.internalName.trim()) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `La zona #${i + 1} no tiene identificador interno (internalName).` });
                return false;
            }

            const internalNameRegex = /^[a-z0-9_]+$/;
            if (!internalNameRegex.test(z.internalName.trim())) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: `El identificador '${z.internalName}' de la zona #${i + 1} no es válido. Solo se permiten letras minúsculas, números y guiones bajos.`
                });
                return false;
            }

            if (internalNames.has(z.internalName.trim())) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: `El identificador interno '${z.internalName}' está duplicado.` });
                return false;
            }
            internalNames.add(z.internalName.trim());
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setActionInProgress(true);

        const cleanZones = zones.map(z => ({
            id: z.id,
            displayName: z.displayName.trim(),
            internalName: z.internalName.trim(),
            color: z.color || '#4CC9F0'
        }));

        const payload = {
            name: listName.trim(),
            zonesJson: JSON.stringify(cleanZones)
        };

        try {
            const url = selectedList 
                ? `${API_URL}v2/zonelists/${selectedList.id}` 
                : `${API_URL}v2/zonelists`;
            const method = selectedList ? 'PUT' : 'POST';

            const res = await authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                showMessage({ 
                    type: MESSAGE_TYPE.SUCCESS, 
                    text: selectedList ? "¡Listado de zonas actualizado!" : "¡Listado de zonas creado correctamente!" 
                });
                if (!selectedList) {
                    handleCancelEdit();
                } else {
                    const updated = await res.json();
                    setSelectedList(updated);
                }
                fetchZoneLists();
            } else {
                const text = await res.text();
                showMessage({ type: MESSAGE_TYPE.BAD, text: `Error: ${text}` });
            }
        } catch (error) {
            console.error("Error saving zone list:", error);
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error de conexión." });
        } finally {
            setActionInProgress(false);
        }
    };

    const openConfirmationModal = (messageToDisplay, actionFunctionToExecute, yesButtonText) => {
        setConfirmModalData({
            message: messageToDisplay,
            actionToYesButton: actionFunctionToExecute,
            textYesButton: yesButtonText || 'Sí, confirmar',
            textNoButton: 'Cancelar'
        });
        setShowConfirmModal(true);
    };

    const handleDelete = (list) => {
        const executeDelete = async () => {
            try {
                const res = await authenticatedFetch(`${API_URL}v2/zonelists/${list.id}`, {
                    method: 'DELETE'
                });

                if (res.ok) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Listado de zonas eliminado." });
                    handleCancelEdit();
                    fetchZoneLists();
                    return { success: true };
                } else {
                    const text = await res.text();
                    return { success: false, message: text || "No se pudo eliminar el catálogo." };
                }
            } catch (error) {
                console.error("Error deleting zone list:", error);
                return { success: false, message: "Error de red." };
            }
        };

        openConfirmationModal(
            `¿Estás seguro de eliminar el listado "${list.name}"? Esto solo será posible si no está asignado a ninguna sala activa.`,
            executeDelete,
            "Sí, eliminar listado"
        );
    };

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            <div className="events-manager-view">
                <header className="events-manager-header d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h1 className="fw-black"><i className="bi bi-geo-alt-fill textADSL--Red"></i> Catálogos de Listados de Zonas</h1>
                        <p className="text-muted mb-0">Gestión centralizada de listados de zonas para reutilizar en múltiples salas.</p>
                    </div>
                    <button onClick={getOutOfTheDynamic} className="btn btn-outline-warning btn-sm">
                        <i className="bi bi-arrow-left me-1"></i> Volver a Eventos y Salas
                    </button>
                </header>

                <div className="events-manager-grid">
                    {/* PANEL IZQUIERDO: LISTADOS EXISTENTES */}
                    <div className="events-section card--glass">
                        <h2 className="section-title"><i className="bi bi-journal-text"></i> Listados Registrados</h2>

                        {loading ? (
                            <div className="loader-container">
                                <div className="spinner-border text-danger" role="status"></div>
                                <p>Cargando listados...</p>
                            </div>
                        ) : zoneLists.length === 0 ? (
                            <p className="empty-message">No hay catálogos de zonas creados.</p>
                        ) : (
                            <ul className="events-list">
                                {zoneLists.map((list) => {
                                    let zoneCount = 0;
                                    try {
                                        zoneCount = JSON.parse(list.zonesJson).length;
                                    } catch (e) {}

                                    return (
                                        <li
                                            key={list.id}
                                            className={`event-item ${selectedList?.id === list.id ? 'active' : ''}`}
                                            onClick={() => handleSelectField(list)}
                                        >
                                            <div className="event-item-info">
                                                <span className="event-name">{list.name}</span>
                                                <span className="event-date">
                                                    <i className="bi bi-layers"></i> {zoneCount} zonas configuradas
                                                </span>
                                            </div>
                                            <span className="badge bg-secondary">
                                                ID: {list.id.substring(0, 5)}...
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* PANEL CENTRAL-DERECHO: CREAR / EDITAR LISTADO */}
                    <div className="create-section card--glass" style={{ gridColumn: 'span 2' }}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="section-title mb-0">
                                <i className={selectedList ? "bi bi-pencil-square" : "bi bi-plus-circle"}></i>{' '}
                                {selectedList ? `Editar Listado: ${selectedList.name}` : "Crear Listado de Zonas"}
                            </h2>
                            {selectedList && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link textADSL--Red fw-semibold p-0"
                                    onClick={handleCancelEdit}
                                >
                                    <i className="bi bi-plus-circle"></i> Nuevo Listado
                                </button>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} className="events-form">
                            <div className="form-group mb-3">
                                <label className="form-label">Nombre del Catálogo</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Ej. Estadio Alfonso Lastras"
                                    value={listName}
                                    onChange={(e) => setListName(e.target.value)}
                                    disabled={actionInProgress}
                                />
                            </div>

                            <div className="zones-editor-container mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="form-label mb-0 fw-bold">Zonas que integran el listado</label>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        onClick={handleAddZone}
                                        disabled={actionInProgress}
                                    >
                                        <i className="bi bi-plus-lg"></i> Añadir Zona
                                    </button>
                                </div>

                                {zones.length === 0 ? (
                                    <p className="text-muted small text-center my-3 border border-dashed p-3 rounded">
                                        No hay zonas en este listado. Haz clic en "Añadir Zona" para empezar.
                                    </p>
                                ) : (
                                    <div className="zones-scroll-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        {zones.map((zone, idx) => (
                                            <div key={zone.tempId} className="zone-editor-row d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-dark-opacity">
                                                <span className="fw-bold text-muted" style={{ minWidth: '24px' }}>#{idx + 1}</span>
                                                
                                                <div className="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm form-input"
                                                        placeholder="Etiqueta (ej. Tema Lucho)"
                                                        value={zone.displayName}
                                                        onChange={(e) => handleZoneChange(zone.tempId, 'displayName', e.target.value)}
                                                        disabled={actionInProgress}
                                                    />
                                                </div>

                                                <div className="flex-grow-1">
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm form-input"
                                                        placeholder="Clave (ej. tema_lucho)"
                                                        value={zone.internalName}
                                                        onChange={(e) => handleZoneChange(zone.tempId, 'internalName', e.target.value)}
                                                        disabled={actionInProgress}
                                                    />
                                                </div>

                                                <div className="d-flex align-items-center gap-1">
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color form-control-sm"
                                                        title="Elegir color"
                                                        value={zone.color}
                                                        onChange={(e) => handleZoneChange(zone.tempId, 'color', e.target.value)}
                                                        disabled={actionInProgress}
                                                        style={{ width: '38px', height: '31px', padding: '2px' }}
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm form-input"
                                                        placeholder="#hex"
                                                        value={zone.color}
                                                        onChange={(e) => handleZoneChange(zone.tempId, 'color', e.target.value)}
                                                        disabled={actionInProgress}
                                                        style={{ width: '75px', fontSize: '12px' }}
                                                    />
                                                </div>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleRemoveZone(zone.tempId)}
                                                    disabled={actionInProgress}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="Btn__Adsl--Red fw-bold w-100 mb-2"
                                disabled={actionInProgress || !listName.trim() || zones.length === 0}
                            >
                                {actionInProgress ? 'Guardando...' : (selectedList ? 'Guardar Cambios' : 'Crear Catálogo')}
                            </button>

                            {selectedList && (
                                <button
                                    type="button"
                                    className="Btn__Adsl--RedOutline w-100 fw-bold"
                                    onClick={() => handleDelete(selectedList)}
                                    disabled={actionInProgress}
                                >
                                    <i className="bi bi-trash-fill"></i> Eliminar Catálogo
                                </button>
                            )}
                        </form>
                    </div>
                </div>
            </div>

            {showConfirmModal && (
                <Confirm
                    message={confirmModalData?.message}
                    actionToYesButton={confirmModalData?.actionToYesButton}
                    textYesButton={confirmModalData?.textYesButton}
                    textNoButton={confirmModalData?.textNoButton}
                    setShowModal={setShowConfirmModal}
                    setConfirmData={setConfirmModalData}
                />
            )}
        </SecondaryBase>
    );
}
